import { type RefObject, useCallback, useState } from 'react';

import DoneIcon from '@mui/icons-material/Done';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Box,
    Button,
    IconButton,
    Popover,
    Stack,
    Table,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    styled
} from '@mui/material';
import { addSeconds } from 'date-fns';
import dateFormat from 'date-fns-tz/format';
import { useTranslation } from 'react-i18next';

import { DeleteRecordingDialog } from './DeleteRecordingDialog';
import { EditRecordingDialog } from './EditRecordingDialog';
import { RecordingLinksDialog } from './RecordingLinksDialog';
import { getRecordingName } from './util';
import { ViewAnalyticsDialog } from './ViewAnalyticsDialog';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { useTransmitUserDetails } from '../../hooks/useTransmission';
import { type ArtifactType, RecordingStatus } from '../../types/recordingTypes';
import { copyToClipboard } from '../../utils/clipboard';
import { getUrls } from '../../utils/url';
import { NoRecords } from '../classroom/SharedComponents';
import { type ClassRecording, type Permission } from '../classroom/types';
import { type RecordingDialogProps } from '../classroom/ui-types';
import { getRecordingArtifact, getRecordingInfo } from '../classroom/util';
import Loadable from '../common/loadable';
import { ActionMenu, type ActionMenuItem } from '../common/table/ActionMenu';

const StyledPopover = styled(Popover)`
    .MuiPopover-paper {
        background-color: #616161 !important;
        color: #ffffff !important;
    }
`;

const ActionsBox = styled(Stack)`
    flex-direction: row;
    align-items: center;
    gap: 4px;
`;

const TableCellTime = styled(TableCell)`
    width: 20%;
`;

const TableCellName = styled(TableCell)`
    width: 50%;
`;

const TableCellDuration = styled(TableCell)`
    width: 15%;
`;

const TableCellStatus = styled(TableCell)`
    width: 15%;
`;

const TableCellAction = styled(TableCell)`
    width: auto;
`;

const TableCellNoWrap = styled(TableCell)`
    white-space: nowrap;
`;

const LoadableButtonWrapper = styled(Loadable)`
    &[aria-busy='true'] {
        .loading-overlay {
            align-items: start;
        }
        .MuiCircularProgress-root {
            width: 16px !important;
            height: 16px !important;
            margin-left: 8px;
        }
        button.MuiButtonBase-root {
            padding-left: 24px;
            padding-right: 8px;
        }
    }
`;

type ClassRecordingExtended = ClassRecording & {
    isLoading?: boolean;
};

type RecordingsTableProps = {
    recordings: ClassRecordingExtended[];
    setRecordings: (recordings: ClassRecordingExtended[]) => void;
    isLoading: boolean;
    isFetching: boolean;
    refetch?: () => void;
    classUuid: string;
    userUuid: string;
    isInstructor: boolean;
    isGuest: boolean;
};

export const RecordingsTable = (props: RecordingsTableProps) => {
    const { recordings, setRecordings, isInstructor, isLoading, isFetching, refetch, isGuest, classUuid, userUuid } =
        props;

    const { t } = useTranslation();

    const { data: orgSettings } = useOrgSettings();

    const { data: schoolInfo } = useSchoolInfo();
    const { transmitUserDetails } = useTransmitUserDetails();

    const [recordingUuid, setRecordingUuid] = useState<string | undefined>();
    const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
    const [popoverCopyLinkOpen, setPopoverCopyLinkOpen] = useState(false);
    const [popoverAnchorEl, setPopoverAnchorEl] = useState<RefObject<unknown> | null>(null);

    const handleAnalyticsDialogClosed = () => {
        setAnalyticsDialogOpen(false);
    };

    const initDialogProps: RecordingDialogProps = {
        open: false,
        downloading: false,
        recording: undefined,
        recordings,
        setRecordings,
        // Note, we can't get classUuid / userUuid from the ClassContext because it will prevent admin
        // users from accessing classes where they are not enrolled.
        // So we prop drill here instead. TODO: We could create a context just for the table to avoid
        // prop drilling.
        classUuid,
        userUuid
    };

    const [deleteDialogProps, setDeleteDialogProps] = useState<RecordingDialogProps>({
        ...initDialogProps
    });

    const [linksDialogProps, setLinksDialogProps] = useState<RecordingDialogProps>({
        ...initDialogProps
    });

    const [editDialogProps, setEditDialogProps] = useState<RecordingDialogProps>({
        ...initDialogProps
    });

    const handleActionRefresh = useCallback(
        async (recordingId: string) => {
            const info = await getRecordingInfo(schoolInfo!.uuid!, userUuid, classUuid, recordingId);
            if (info) {
                console.log('Recording refreshed');
                setRecordings(
                    recordings.map((rec) =>
                        rec.recordingId === recordingId
                            ? { ...rec, status: info.status, duration: info.duration ?? 0 }
                            : rec
                    )
                );
            }
        },
        [schoolInfo, userUuid, classUuid, recordings, setRecordings]
    );

    const getRecordingLinkForRecording = useCallback(
        (recording: ClassRecording): string => {
            const url = new URL(`${getUrls().vanityUrl}/player/recording/${classUuid}/${recording.recordingId}`);
            return url.toString();
        },
        [classUuid]
    );

    const handleCopyLink = useCallback(
        async (uniqueId: string, refEl: RefObject<unknown>) => {
            const recording = recordings.find((r) => r.recordingId === uniqueId);
            const url = getRecordingLinkForRecording(recording!);
            await copyToClipboard(url);
            setPopoverAnchorEl(refEl);
            setPopoverCopyLinkOpen(true);
            // Auto close popover after 2 seconds.
            window.setTimeout(() => {
                setPopoverCopyLinkOpen(false);
            }, 2000);
        },
        [recordings, getRecordingLinkForRecording]
    );

    const handleLaunch = useCallback(
        (recording: ClassRecording) => {
            if (recording.status === 'AVAILABLE') {
                transmitUserDetails(new URL(getRecordingLinkForRecording(recording)));
                return;
            }

            if (!schoolInfo) {
                throw new Error('Failed to launch - schoolInfo not available');
            }

            const setRecordingLoading = (recordingId: string, isLoading: boolean) => {
                const index = recordings.findIndex((r) => r.recordingId === recordingId);
                if (index === -1) return recordings; // Return early if not found

                const updatedRecordings = [...recordings];
                updatedRecordings[index] = { ...updatedRecordings[index], isLoading };

                setRecordings(updatedRecordings);
            };

            const launchExternal = async () => {
                setRecordingLoading(recording.recordingId, true);
                // Preffered artifact types in order of preference.
                const preferredArtifactTypes: ArtifactType[] = [
                    'composite',
                    'shared_screen_with_speaker_view(CC)',
                    'shared_screen_with_speaker_view',
                    'shared_screen_with_gallery_view',
                    'shared_screen',
                    'active_speaker',
                    'host_video',
                    'gallery_view',
                    'audio_only'
                ];

                const recordingInfo = await getRecordingInfo(
                    schoolInfo.uuid,
                    userUuid,
                    classUuid,
                    recording.recordingId
                );

                if (!recordingInfo?.artifacts) {
                    setRecordingLoading(recording.recordingId, false);
                    return;
                }

                let artifactType: ArtifactType | undefined;
                for (const p in preferredArtifactTypes) {
                    const prefType = preferredArtifactTypes[p];
                    if (Object.keys(recordingInfo.artifacts).includes(prefType)) {
                        artifactType = prefType as ArtifactType;
                        break;
                    }
                }
                if (!artifactType) {
                    throw new Error('Unable to find a suitable recording artifact to play');
                }

                const artifact = await getRecordingArtifact(
                    schoolInfo.uuid,
                    userUuid,
                    classUuid,
                    recording.recordingId,
                    artifactType
                );
                if (!artifact) {
                    throw new Error('Failure to get recording artifact');
                }
                setRecordingLoading(recording.recordingId, false);
                (window as any).open(artifact.url, '_blank').focus();
            };
            launchExternal();
        },
        [getRecordingLinkForRecording, transmitUserDetails, userUuid, classUuid, schoolInfo, recordings, setRecordings]
    );

    const handleExternalLink = useCallback(
        (uniqueId: string) => {
            const recording = recordings.find((r) => r.recordingId === uniqueId);
            setLinksDialogProps((current) => ({ ...current, downloading: false, recording, open: true }));
        },
        [recordings]
    );
    const handleActionDownload = useCallback(
        (uniqueId: string) => {
            const recording = recordings.find((r) => r.recordingId === uniqueId);
            setLinksDialogProps((current) => ({ ...current, downloading: true, recording, open: true }));
        },
        [recordings]
    );

    const handleActionEdit = useCallback(
        (uniqueId: string) => {
            const recording = recordings.find((r) => r.recordingId === uniqueId);
            setEditDialogProps((current) => ({ ...current, recording, open: true }));
        },
        [recordings]
    );

    const handleActionDelete = useCallback(
        (uniqueId: string) => {
            const recording = recordings.find((r) => r.recordingId === uniqueId);
            setDeleteDialogProps((current) => ({ ...current, recording, open: true }));
        },
        [recordings]
    );

    const handleActionViewAnalytics = useCallback((uniqueId: string) => {
        setAnalyticsDialogOpen(true);
        setRecordingUuid(uniqueId);
    }, []);

    function getActions(recording: ClassRecording): ActionMenuItem[] {
        const items: ActionMenuItem[] = [{ name: t('common:refresh'), onClick: handleActionRefresh }];
        const downloadsDisabled = Boolean(orgSettings?.featureSettings.options.recordingsDisableDownloads);

        if (isInstructor) {
            items.push({ name: t('lti:recordings.edit-settings'), onClick: handleActionEdit });
        }

        if (recording.status === RecordingStatus.EXTERNAL) {
            items.push({ name: t('lti:recordings.external-link'), onClick: handleExternalLink });

            if (isInstructor) {
                items.push({ name: t('common:delete'), onClick: handleActionDelete });
            }
        } else if (recording.status === RecordingStatus.AVAILABLE) {
            items.push({ name: t('lti:recordings.copy-link'), onClick: handleCopyLink });
            if (!downloadsDisabled &&
                (isInstructor || orgSettings?.recordingSettings.participantsCanDownload)) {
                items.push({ name: t('common:download'), onClick: handleActionDownload });
            }

            if (isInstructor) {
                items.push({ name: t('lti:recordings.view-analytics'), onClick: handleActionViewAnalytics });
                items.push({ name: t('common:delete'), onClick: handleActionDelete });
            }
        }

        return items;
    }

    const recordingTimes = (recording: ClassRecording) => {
        const hasDuration = recording.duration && Number(recording.duration) > 0;
        const startDate = new Date(recording.when);
        const endDate = hasDuration ? addSeconds(new Date(recording.when), recording.duration) : null;
        let startDateStr = dateFormat(startDate, 'PPPP');
        let endDateStr = endDate ? dateFormat(endDate, 'PPPP') : null;
        let date = startDateStr;
        if (startDateStr !== endDateStr && endDate !== null) {
            // The recording spans two dates!
            // Use short date formats for range.
            startDateStr = dateFormat(startDate, 'P');
            endDateStr = dateFormat(endDate, 'P');
            date = `${startDateStr} - ${endDateStr}`;
        }
        const endTimeStr = endDate !== null ? dateFormat(endDate, 'p') : null;
        let times;
        if (endTimeStr) {
            times = `${dateFormat(startDate, 'p')} - ${endTimeStr}`;
        } else {
            // No valid duration.
            times = `${dateFormat(startDate, 'p')}`;
        }
        return (
            <>
                <Box>{date}</Box>
                <Box>{times}</Box>
            </>
        );
    };

    const recordingDuration = (recording: ClassRecording) => {
        if (!recording.duration) {
            return null;
        }
        // https://codingbeautydev.com/blog/javascript-convert-seconds-to-hours-and-minutes/

        // Note - we are calculating hours, minutes and seconds this way instead of using dateFns because it is
        // possible that a recording could go beyond 24 hours.
        const totalSeconds = recording.duration;
        const totalMinutes = Math.floor(totalSeconds / 60);
        const seconds = (totalSeconds % 60).toFixed(0);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const secondsStr = `${seconds}`.padStart(2, '0');
        const hoursStr = `${hours}`.padStart(2, '0');
        const minutesStr = `${minutes}`.padStart(2, '0');

        return (
            <Box>
                {hoursStr}:{minutesStr}:{secondsStr}
            </Box>
        );
    };

    const permissionsString = (permission: Permission) => t(`lti:recordings.permissions.${permission.toLowerCase()}`);

    return (
        <Loadable fullScreen={false} isLoading={isLoading}>
            <ViewAnalyticsDialog
                open={analyticsDialogOpen}
                handleClose={handleAnalyticsDialogClosed}
                recordingUuid={recordingUuid}
                classUuid={classUuid}
                userUuid={userUuid}
            />

            <DeleteRecordingDialog
                {...deleteDialogProps}
                onClose={() => setDeleteDialogProps((current) => ({ ...current, open: false }))}
                recordings={recordings}
            />

            <EditRecordingDialog
                {...editDialogProps}
                onClose={() => setEditDialogProps((current) => ({ ...current, open: false }))}
                recordings={recordings}
            />

            <RecordingLinksDialog
                {...linksDialogProps}
                onClose={() => setLinksDialogProps((current) => ({ ...current, open: false }))}
                recordings={recordings}
            />

            <Table>
                <TableHead>
                    <TableRow>
                        <TableCellTime>{t('class:time')}</TableCellTime>
                        <TableCellName>{t('class:name')}</TableCellName>
                        <TableCellDuration>{t('class:duration')}</TableCellDuration>
                        <TableCellStatus>{t('class:whocanview')}</TableCellStatus>
                        <TableCellAction>{t('class:actions')}</TableCellAction>
                    </TableRow>

                    {recordings &&
                        recordings.map((recording) => (
                            <TableRow key={recording.recordingId}>
                                <TableCellNoWrap>{recordingTimes(recording)}</TableCellNoWrap>
                                <TableCell>{getRecordingName(t, recording)}</TableCell>
                                <TableCellNoWrap>{recordingDuration(recording)}</TableCellNoWrap>
                                <TableCell>{permissionsString(recording.permission)}</TableCell>
                                <TableCell>
                                    <ActionsBox>
                                        <ActionMenu
                                            uniqueId={recording.recordingId}
                                            title={t('lti:recordings.actions')}
                                            actions={getActions(recording)}
                                            sx={{ py: '2px', px: '3px' }}
                                        />

                                        {!isGuest && ['AVAILABLE', 'EXTERNAL'].includes(recording.status) ? (
                                            <LoadableButtonWrapper isLoading={recording.isLoading}>
                                                <Button
                                                    sx={{ minWidth: '140px' }}
                                                    color="primary"
                                                    variant="contained"
                                                    onClick={() => handleLaunch(recording)}
                                                >
                                                    {t('lti:recordings.launch')}
                                                </Button>
                                            </LoadableButtonWrapper>
                                        ) : (
                                            <Box sx={{ width: '100%', textAlign: 'center', fontSize: '14px' }}>
                                                {t(`lti:recordings.statusShort.${recording.status.toLowerCase()}`)}
                                            </Box>
                                        )}
                                    </ActionsBox>
                                </TableCell>
                            </TableRow>
                        ))}
                </TableHead>
            </Table>

            {recordings.length === 0 && (
                <NoRecords message={t('lti:recordings.noneformeeting')}>
                    {refetch && (
                        <IconButton onClick={() => refetch()} disabled={isFetching} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    )}
                </NoRecords>
            )}

            <StyledPopover
                id="copy-link"
                open={popoverCopyLinkOpen}
                anchorEl={popoverAnchorEl?.current as HTMLElement}
                onClose={() => setPopoverCopyLinkOpen(false)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
            >
                <Typography sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center' }}>
                    {t('common:copiedlink')}
                    <DoneIcon sx={{ color: '#fff', marginLeft: '5px' }} />
                </Typography>
            </StyledPopover>
        </Loadable>
    );
};
